import { z } from "zod";

import { isBlockedFileExtension, isVideoMimeType, resolveUploadMimeType } from "./file-types";
import { DocumentFileFingerprintSchema } from "./schemas";

export const MAX_NON_VIDEO_UPLOAD_BYTES = 50 * 1024 * 1024;
export const MAX_VIDEO_UPLOAD_BYTES = 250 * 1024 * 1024;
export const MAX_FILES_PER_UPLOAD_REQUEST = 50;
export const DOCUMENT_ARCHIVE_KEY_PREFIX = "_archives/";

const RelatedEntityTypeSchema = z.enum(["case", "client"]);

const validateUploadFileSize = (
  { filename, mimeType, sizeBytes }: { filename: string; mimeType: string; sizeBytes: number },
  ctx: z.RefinementCtx,
) => {
  const resolvedMimeType = resolveUploadMimeType(filename, mimeType);
  if (!isVideoMimeType(resolvedMimeType) && sizeBytes > MAX_NON_VIDEO_UPLOAD_BYTES) {
    ctx.addIssue({
      code: "custom",
      path: ["sizeBytes"],
      message: "non-video uploads must not exceed 50 MB",
    });
  }
};

const validateUploadFileType = ({ filename }: { filename: string }, ctx: z.RefinementCtx) => {
  if (isBlockedFileExtension(filename)) {
    ctx.addIssue({
      code: "custom",
      path: ["filename"],
      message: "this file type is not allowed",
    });
  }
};

const UploadUrlFileSchema = z
  .object({
    documentType: z
      .string()
      .min(1)
      .max(80)
      .optional()
      .describe("Provided when uploading to a specific slot; omitted for general drops."),
    filename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(255),
    fileFingerprint: DocumentFileFingerprintSchema,
    sizeBytes: z.number().int().positive().max(MAX_VIDEO_UPLOAD_BYTES),
  })
  .superRefine(validateUploadFileSize)
  .superRefine(validateUploadFileType);

const GenerateUploadUrlBaseSchema = z.object({
  relatedEntityId: z.uuid(),
  relatedEntityType: RelatedEntityTypeSchema,
});

export const GenerateUploadUrlRequestSchema = GenerateUploadUrlBaseSchema.extend(
  UploadUrlFileSchema.shape,
)
  .superRefine(validateUploadFileSize)
  .superRefine(validateUploadFileType);
export type GenerateUploadUrlRequest = z.infer<typeof GenerateUploadUrlRequestSchema>;

export const GenerateUploadUrlsRequestSchema = GenerateUploadUrlBaseSchema.extend({
  files: z.array(UploadUrlFileSchema).min(1).max(MAX_FILES_PER_UPLOAD_REQUEST),
});
export type GenerateUploadUrlsRequest = z.infer<typeof GenerateUploadUrlsRequestSchema>;
